import { makeAutoObservable, reaction, runInAction } from "mobx";
import type { ApiClient } from "@/clients/ApiClient";
import type { Playlist, PlaylistDetail, PlaylistsResponse, PlaylistSummary } from "@/generated/api";

type ListsApi = Pick<ApiClient, "get" | "post" | "put" | "patch" | "delete">;

interface ListsAuth {
    readonly isSignedIn: boolean;
}

/**
 * The listener's named lists, beside the saved list ("Spara i spellistan"). Only signed in: the lists live on
 * the account, so signed out there are none. Every change is sent to the API, then the lists are read again, so
 * the counts are the server's.
 */
export class ListsStore {
    lists: Playlist[] = [];
    loaded = false;

    constructor(
        private readonly client: ListsApi,
        private readonly auth: ListsAuth
    ) {
        makeAutoObservable<ListsStore, "client" | "auth">(this, { client: false, auth: false });

        reaction(
            () => this.auth.isSignedIn,
            (signedIn) => {
                if (!signedIn) {
                    this.lists = [];
                    this.loaded = false;
                }
            }
        );
    }

    /** The saved list first, then the named lists in the order they were made */
    get named(): Playlist[] {
        return this.lists.filter((list) => !list.saved);
    }

    /** Reads the lists from the account */
    async load() {
        if (!this.auth.isSignedIn) return;
        const response = await this.client.get<PlaylistsResponse>("/me/playlists");
        runInAction(() => {
            this.lists = response.playlists;
            this.loaded = true;
        });
    }

    async create(name: string): Promise<Playlist> {
        const response = await this.client.post<PlaylistSummary>("/me/playlists", { name });
        await this.load();
        return response.playlist;
    }

    async rename(id: number, name: string) {
        await this.client.patch<PlaylistSummary>(`/me/playlists/${id}`, { name });
        await this.load();
    }

    async remove(id: number) {
        await this.client.delete(`/me/playlists/${id}`);
        await this.load();
    }

    /** One list with its episodes in order */
    detail(id: number): Promise<PlaylistDetail> {
        return this.client.get<PlaylistDetail>(`/me/playlists/${id}/items`);
    }

    /** Adds episodes to the end of a list; episodes already in it stay where they are */
    async add(id: number, episodeIds: number[]) {
        await this.client.post(`/me/playlists/${id}/items`, { episode_ids: episodeIds });
        await this.load();
    }

    /** Makes a list exactly these episodes, in this order */
    async reorder(id: number, episodeIds: number[]) {
        await this.client.put(`/me/playlists/${id}/items`, { episode_ids: episodeIds });
    }

    async removeEpisode(id: number, episodeId: number) {
        await this.client.delete(`/me/playlists/${id}/items/${episodeId}`);
        await this.load();
    }
}
